import {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetRentalOrderQuery} from '@dms/api/rental';
import i18n from '@dms/i18n';
import {rentalOrderRoutes, testIds} from '@dms/routes';
import {NavigationItem, Page} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import {ActivityLogs} from './(Sections)/ActivityLogs/ActivityLogs';
import {Documents} from './(Sections)/Documents/Documents';
import {RentalOrderHeader} from './(Sections)/RentalOrderHeader/RentalOrderHeader';
import {RentalOrderPhotos} from './(Sections)/RentalOrderPhotos/RentalOrderPhotos';
import {RentVehicle} from './(Sections)/RentVehicle/RentVehicle';
import {ReturnVehicle} from './(Sections)/ReturnVehicle/ReturnVehicle';
import {ServiceOrder} from './(Sections)/ServiceOrder/ServiceOrder';
import {VehicleAndCustomer} from './(Sections)/VehicleAndCustomer/VehicleAndCustomer';

type NavigationRoute =
  | typeof rentalOrderRoutes.vehicleAndCustomer
  | typeof rentalOrderRoutes.returnVehicle
  | typeof rentalOrderRoutes.rentVehicle
  | typeof rentalOrderRoutes.documents
  | typeof rentalOrderRoutes.activityLogs
  | typeof rentalOrderRoutes.photos
  | typeof rentalOrderRoutes.serviceOrder;

export function RentalOrderDetail() {
  const navigate = useNavigate();
  const {id: defaultRentalOrderId} = useParams();
  const [rentalOrderId, setRentalOrderId] = useState(defaultRentalOrderId);

  useEffect(() => {
    setRentalOrderId(defaultRentalOrderId);
  }, [defaultRentalOrderId]);

  const {data: rentalOrder, isLoading: isLoadingRentalOrder} = useGetRentalOrderQuery(
    {rentalOrderId: rentalOrderId ?? ''},
    {skip: isNil(rentalOrderId), refetchOnMountOrArgChange: true}
  );

  const handleSetRentalOrderId = (rentalOrderId: string | undefined) => {
    if (isNil(rentalOrderId)) {
      throw new Error('Rental order ID is required');
    }

    setRentalOrderId(rentalOrderId);
    navigate(composePath(rentalOrderRoutes.vehicleAndCustomer, {params: {id: rentalOrderId}}), {
      replace: true,
    });
  };

  const getNavigationHref = (route: NavigationRoute) => {
    if (isNil(rentalOrderId)) {
      return rentalOrderRoutes.create;
    }

    return composePath(route, {
      params: {id: rentalOrderId},
    });
  };

  const rentInspectionId = rentalOrder?.rentVehicleInspections?.[0];
  const returnInspectionId = rentalOrder?.returnVehicleInspections?.[0];

  const hasNoInspections = isNilOrEmpty(rentInspectionId) && isNilOrEmpty(returnInspectionId);

  const navigationItems: NavigationItem[] = [
    {
      id: 'rental-order-vehicleAndCustomer',
      label: i18n.t('page.rentalOrder.labels.vehicleAndCustomer'),
      hasCounter: true,
      href: getNavigationHref(rentalOrderRoutes.vehicleAndCustomer),
      content: (
        <VehicleAndCustomer
          rentalOrderId={rentalOrderId}
          setRentalOrderId={handleSetRentalOrderId}
        />
      ),
      'data-testid': testIds.rentalOrder.vehicleAndCustomer('navigation-vehicleAndCustomer'),
    },
    {
      id: 'rental-order-rentVehicle',
      hasCounter: true,
      isDisabled: isNil(rentalOrder) || rentalOrder?.state === 'draft',
      label: i18n.t('page.rentalOrder.labels.rentVehicle'),
      href: getNavigationHref(rentalOrderRoutes.rentVehicle),
      content: [
        {route: rentalOrderRoutes.rentVehicle, element: <RentVehicle />},
        {route: rentalOrderRoutes.rentVehicleInspections, element: <RentVehicle />},
      ],
      'data-testid': testIds.rentalOrder.rentVehicle('navigation-rentVehicle'),
    },
    {
      id: 'rental-order-returnVehicle',
      hasCounter: true,
      hasSeparator: true,
      isDisabled:
        isNil(rentalOrder) || rentalOrder?.state === 'draft' || rentalOrder?.state === 'reserved',
      label: i18n.t('page.rentalOrder.labels.returnVehicle'),
      href: getNavigationHref(rentalOrderRoutes.returnVehicle),
      content: [
        {route: rentalOrderRoutes.returnVehicle, element: <ReturnVehicle />},
        {route: rentalOrderRoutes.returnVehicleInspections, element: <ReturnVehicle />},
      ],
      'data-testid': testIds.rentalOrder.returnVehicle('navigation-returnVehicle'),
    },
    {
      id: 'rental-order-serviceOrder',
      label: i18n.t('page.rentalOrder.labels.serviceOrder'),
      href: getNavigationHref(rentalOrderRoutes.serviceOrder),
      isDisabled: isNil(rentalOrder) || rentalOrder?.state === 'draft',
      content: <ServiceOrder />,
      'data-testid': testIds.rentalOrder.serviceOrder('navigation-serviceOrder'),
    },
    {
      id: 'rental-order-photos',
      label: i18n.t('page.rentalOrder.labels.photos'),
      href: getNavigationHref(rentalOrderRoutes.photos),
      isDisabled: isNil(rentalOrder) || rentalOrder?.state === 'draft' || hasNoInspections,
      content: <RentalOrderPhotos />,
      'data-testid': testIds.rentalOrder.photos('navigation-photos'),
    },
    {
      id: 'rental-order-documents',
      isDisabled: isNil(rentalOrder) || rentalOrder?.state === 'draft',
      label: i18n.t('page.rentalOrder.labels.documents'),
      href: getNavigationHref(rentalOrderRoutes.documents),
      content: <Documents />,
      'data-testid': testIds.rentalOrder.documents('navigation-documents'),
    },
    {
      id: 'rental-order-activityLogs',
      isDisabled: isNil(rentalOrder) || rentalOrder?.state === 'draft',
      label: i18n.t('page.rentalOrder.labels.activityLogs'),
      href: getNavigationHref(rentalOrderRoutes.activityLogs),
      content: [
        {route: rentalOrderRoutes.activityLogs, element: <ActivityLogs />},
        {route: rentalOrderRoutes.emailActivityLogs, element: <ActivityLogs />},
      ],
      'data-testid': testIds.rentalOrder.activityLogs('navigation-activityLogs'),
    },
  ];

  return (
    <Page
      key={rentalOrderId}
      navigation={navigationItems}
      isLoading={isLoadingRentalOrder}
      header={
        <RentalOrderHeader
          rentalOrderId={rentalOrderId}
          data-testid={testIds.rentalOrder.detail('header')}
        />
      }
      data-testid={testIds.rentalOrder.detail('page')}
    />
  );
}
